"use client";
// ════════════════════════════════════════════════════════════════════════════
// BÁO LỖI TRÌNH DUYỆT (F12) → HỘP LỖI TRUNG TÂM SugaHub.
// Nghe lỗi client CHƯA-BẮT (window 'error' + 'unhandledrejection') rồi đẩy METADATA về
// /api/client-error (CÙNG origin → khỏi CORS/khoá) → trạm đó forward lên SugaHub qua ống server.
// Dùng bộ bắt lỗi CHUNG của lò @suga-co/observe-web/client (mọi app Suga xài chung — Bill chốt 19/07).
//
// Khác ChunkReloadGuard: guard đó CHỈ tự-tải-lại khi lỗi tải-chunk (không báo về hộp lỗi);
// bộ này BÁO mọi lỗi client còn lại (React #418, TypeError…) về hộp lỗi để check & fix.
// Đã lọc sẵn: lỗi chunk, resource 404 (favicon), "Script error." rỗng, khung tiện-ích — khỏi trùng/ồn.
//
// ── 02/08/2026: DẤU VÂN TAY MÔI TRƯỜNG đã CHUYỂN LÊN GÓI CHUNG (0.4.0) ──────
// Trước đây (28/07) hidental tự chép tay: 2 listener riêng (window 'error' + vá console.error)
// + tự dựng dấu vân tay môi trường (trình duyệt/dịch trang/thẻ lạ…), rồi `ignore` cả họ lỗi
// hydrate ở bộ chung để khỏi ra 2 vé. Lý do đo được lúc đó (phiếu #d056ed92): #418 KHÔNG bao giờ
// có tên component trong vết lỗi ở bản thật — thứ còn thiếu là AI ĐÃ SỜ VÀO TRANG (tiện ích
// Grammarly chèn DOM trước khi React gắn tay, không phải bug code).
//
// Sau khi đo lại trên hộp lỗi 02/08 (163 phiếu từ 9 app KHÁC không hề có dòng chẩn đoán này),
// Bill chốt BÊ dấu vân tay lên `@suga-co/observe-web` 0.4.0 — bộ chung giờ TỰ bắt lỗi hydrate
// (cả đường window.onerror lẫn console.error) VÀ tự gắn dấu vân tay vào `message`
// (`⟨môi trường: …⟩`), y hệt bản hidental tự chép trước đây. Xem `moi-truong.ts` trong gói.
// ⇒ Cắt hết bản chép tay ở đây, để bộ chung tự lo — KHÔNG được `ignore` họ lỗi hydrate nữa,
// nếu không tín hiệu #418 sẽ MẤT SẠCH (ignore ở bộ chung + không ai tự báo tay = im lặng).
//
// THỨ CÒN GIỮ RIÊNG (bộ chung không có): 3 bộ lọc nhiễu ở dưới — trình duyệt-trong-app Zalo +
// script luồng của react-dom ($RS/$RC/$RR) + rớt mạng vết TRỤI — và
// appVersion đọc từ NEXT_PUBLIC_APP_VERSION (phải viết nguyên văn để trình đóng gói thay được).
// ════════════════════════════════════════════════════════════════════════════
import { useEffect } from "react";
import { createClientErrorReporter } from "@suga-co/observe-web/client";
import { CAU_FETCH_HONG } from "@/lib/loi/khong-toi-may-chu";

// ── NHIỄU TỪ TRÌNH DUYỆT TRONG APP ZALO (KHÔNG phải lỗi code mình) ──────────
// Khi khách mở link (vd /login) BÊN TRONG app Zalo, app tự CHÈN một script cầu-nối
// vào đầu trang; script đó hụt biến của CHÍNH NÓ ("zaloJSV2" / "ZaloJavaScriptInterface")
// → "Can't find variable: zaloJSV2". Vết lỗi kiểu "global code@…/login:1:21" = script cắm
// ngoài, cột 21 dòng 1. Repo mình KHÔNG hề tham chiếu các biến này (đã grep: 0 kết quả)
// ⇒ ta không sửa được và cũng không nên báo. Lọc để khỏi đẻ vé nhiễu vào hộp lỗi — giống
// cách bộ chung đã lọc "Script error." rỗng và khung tiện-ích. Bộ chung KHÔNG có bộ lọc này
// (đặc thù riêng hidental) nên phải giữ lại qua tham số `ignore`.
const NHIEU_ZALO_IN_APP = /\bzaloJSV2\b|\bZaloJavaScriptInterface\b/;

// ── NHIỄU TỪ SCRIPT LUỒNG (streaming) CỦA CHÍNH REACT — không sửa được bằng code mình ──
// Vé thật gặp 03/08/2026 ở /appointments:
//   TypeError: Cannot read properties of null (reading 'parentNode')
//       at $RS (https://ops.hidental.vn/appointments:1:225439)
//       at         https://ops.hidental.vn/appointments:1:225508
// `$RS` KHÔNG phải hàm của mình (đã grep repo: 0 kết quả). Đó là script NHỎ mà react-dom TỰ
// nhét thẳng vào HTML khi trang render kiểu LUỒNG (server đẩy vỏ trang trước, nội dung chậm
// đẩy sau — đây là cơ chế của app/loading.tsx). Nguyên văn trong react-dom 19:
//   $RS=function(a,b){a=document.getElementById(a);b=document.getElementById(b);
//                     for(a.parentNode.removeChild(a); …)}
// Nó tìm 2 thẻ tạm theo id rồi CHUYỂN nội dung vào đúng chỗ. React KHÔNG kiểm null ở đây, nên
// nếu 2 thẻ tạm đó biến mất khỏi trang trước lúc script chạy thì `a` = null → nổ 'parentNode'.
// Ai làm biến mất? Không phải mình: shell của app không hề gỡ/di chuyển thẻ DOM nào (chỉ có mẹo
// tạo thẻ <a> để tải file rồi tự xoá — đụng đúng thẻ của nó). Thủ phạm quen mặt là thứ SỜ VÀO
// TRANG từ bên ngoài: tiện ích trình duyệt (dịch trang, Grammarly, chặn quảng cáo), trình duyệt
// trong-app (Zalo/Facebook) hay proxy/AV gom-và-lọc HTML giữa đường — CÙNG HỌ với 2 loại nhiễu
// đã ghi nhận ở trên (#418 do Grammarly · zaloJSV2). Ta không vá được code của react-dom, cũng
// không nên tự tải lại trang (rủi ro lặp vô hạn) ⇒ lọc để khỏi đẻ vé nhiễu (chỉ kêu KHI DỒN TRÊN
// CÙNG MỘT TRANG — xem dưới).
//
// ⚠️ LỌC THEO VẾT LỖI (stack), TUYỆT ĐỐI KHÔNG lọc theo message: "Cannot read properties of null
// (reading 'parentNode')" là câu CHUNG CHUNG, code mình cũng có thể sinh ra — lọc theo message
// sẽ NUỐT LUÔN bug thật. Bộ chung thử `ignore` với message và stack RIÊNG (isNoise(msg) ||
// isNoise(stk)), nên mẫu chỉ khớp stack là đủ hẹp. Chỉ nhận 3 script có thật của react-dom và
// KHÔNG kiểm null: $RS (completeSegment) · $RC (completeBoundary) · $RR (…WithStyles).
// ($RX đã tự kiểm `a&&…` nên không bao giờ nổ kiểu này.)
// Khớp cả 2 kiểu vết lỗi: Chrome "    at $RS (url:1:2)" và Safari/Firefox "$RS@url:1:2".
// `(?:[\w$]+\.){0,5}` = TIỀN TỐ NGƯỜI NHẬN của khung Chrome — V8 in kèm chủ thể khi hàm được
// gọi như phương thức: "at Object.$RS (…)" / "at HTMLDocument.$RC (…)". Thiếu nhánh này thì
// `\s*` sau "at " KHÔNG nhảy qua nổi "Object." ⇒ nhiễu vẫn lọt. Chặn {0,5} (thực tế ≤2 tầng)
// cho khỏi lần ngược tốn kém trên vết lỗi dài.
const NHIEU_SCRIPT_LUONG_REACT = /(?:\bat |^)\s*(?:[\w$]+\.){0,5}\$R[SCR]\s*[(@]/m;

// ── RỚT MẠNG LẺ TẺ, VẾT LỖI TRỤI — cú gọi máy chủ không đi được, KHÔNG phải bọ code ─────────
// 🩸 Vé thật 12/08/2026 ở /appointments (bản 2acc6c9), nổ đúng 1 lần, TOÀN BỘ vết lỗi là:
//     TypeError: network error
// Không một khung nào ⇒ không lần được nơi gọi. Soi lại cả /appointments: 4 màn client ở đó
// (NewAppointmentForm · AppointmentRow · AppointmentCard · VisitStatusControl) đều gọi server
// action TRONG try/catch + toast tiếng Việt, ô tìm bệnh nhân cũng có catch ⇒ người dùng KHÔNG
// thấy trang gãy. Đây là wifi phòng khám chập / máy chủ Railway khởi động lại — ta không vá được
// bằng code, mà vé vẫn tốn một vòng người đi truy. ⇒ CÙNG LUẬT với nhiễu $RS: im khi lẻ tẻ, chỉ
// kêu khi DỒN trên cùng một trang.
//
// ⚠️ LỌC THEO VẾT LỖI, TUYỆT ĐỐI KHÔNG THEO CÂU LỖI — luật sống-còn của bộ lọc này. Vé 05/08 ở
// /security/password mang ĐÚNG một câu trong họ ("TypeError: Failed to fetch") nhưng CÓ KHUNG
// (`at M (…/chunks/3200….js)` = `fetchServerAction` của Next), tức một nơi gọi thiếu try/catch làm
// SẬP cả trang — thứ PHẢI đẻ vé, và đã dẫn tới bản vá thật. Lọc theo message là nuốt luôn loại vé
// đó ⇒ lần sau một màn gãy y hệt sẽ chết trong im lặng.
//
// Hai vế, khớp CẢ CHUỖI (KHÔNG cờ `m`) nên chỉ ăn vết TRỤI đúng nghĩa:
//   ① mở đầu bằng `TypeError:` — đây là DÒNG ĐẦU của vết lỗi, không phải message trần. Bộ chung
//      thử `ignore` với message và stack RIÊNG (isNoise(msg) || isNoise(stk)); message của lỗi này
//      chính là "network error" — cũng một dòng, cũng không khung. Đòi có tiền tố `TypeError:` là
//      cách phân biệt "đang soi VẾT LỖI" với "đang soi CÂU LỖI", và cũng khoá luôn vế `TypeError`
//      của `laLoiKhongToiMayChu` (fetch chỉ từ chối bằng TypeError khi hỏng ở tầng mạng).
//   ② `$` cuối, không cờ `m` ⇒ có BẤT KỲ dòng nào nữa (một khung thôi) là TRƯỢT ⇒ ra vé như thường.
// `(.*?)` KHÔNG ăn được xuống dòng (không cờ `s`), `\.?` nuốt dấu chấm cuối câu kiểu Firefox
// ("…fetch resource.") để còn so với mẫu trong họ.
const VET_TRUI_MOT_DONG = /^TypeError:\s*(.*?)\s*\.?\s*$/i;

/**
 * Vết lỗi này có phải "rớt mạng, vết TRỤI" không? = ① đúng hình dạng một dòng `TypeError: <câu>`
 * (xem `VET_TRUI_MOT_DONG`) VÀ ② `<câu>` đó thuộc họ `CAU_FETCH_HONG` của lib/loi/khong-toi-may-chu.ts
 * — một nguồn duy nhất, thêm câu ở đó là chỗ này biết ngay (đọc lúc CHẠY, không phải lúc nạp module).
 *
 * ⚠️ TUYỆT ĐỐI KHÔNG ghép cả họ thành một mẫu kiểu `CAU_FETCH_HONG.map((m) => m.source).join("|")`
 * rồi nhét vào giữa `^TypeError:… $` — đó là đường CHẾT CÂM:
 *   · mẫu nào trong họ có NEO (`/^Load failed$/`) thì nguồn của nó mang theo `^`/`$` vào GIỮA mẫu
 *     ghép ⇒ `^TypeError:\s*(?:^load failed$|…)\s*$` KHÔNG BAO GIỜ khớp. Không ai thấy: bộ lọc chỉ
 *     im lặng ngừng lọc, còn vé rớt mạng vẫn về đều — đúng thứ nó sinh ra để chặn;
 *   · cờ KHÔNG đi theo `.source`: mẫu có `u` (`\p{…}`, `\u{…}`) ghép vào mẫu không-`u` là sai nghĩa
 *     hoặc NÉM ngay lúc nạp module.
 * Cách ở đây thử TỪNG mẫu với chính cờ của nó, và đòi khớp TRỌN câu (`khop[0] === cau`) để giữ đúng
 * độ hẹp của bản ghép cũ — "TypeError: cannot load failed thing" KHÔNG được coi là rớt mạng.
 * Lệch duy nhất so với bản ghép: mẫu có nhánh mà nhánh trước là tiền tố của nhánh sau (`/x|xx/`)
 * thì `exec` chốt nhánh ngắn ⇒ ta bắt HỤT. Hụt = ra vé thừa, chấp nhận được; nuốt oan mới là mất
 * tín hiệu (cùng chiều nghiêng với `laLoiKhongToiMayChu`).
 */
function laVetTruiMatMang(vet: string): boolean {
  const cau = VET_TRUI_MOT_DONG.exec(vet)?.[1];
  if (!cau) return false;
  return CAU_FETCH_HONG.some((mau) => {
    mau.lastIndex = 0; // phòng mẫu lỡ mang cờ `g`/`y` (nhớ `lastIndex` ⇒ khớp lúc có lúc không)
    return mau.exec(cau)?.[0] === cau;
  });
}

// ── IM khi LẺ TẺ · CHỈ KÊU khi DỒN TRÊN CÙNG MỘT TRANG ─────────────────────
// 03/08 ta chưa dám nuốt trắng nên GIỮ vé đầu tiên mỗi phiên tab làm MẪU, để còn xem lỗi này còn
// xảy ra không và ở route nào. MẪU ĐÃ TRẢ LỜI XONG (05/08, phiếu #88dbaf72):
//   · 03/08 · /appointments      · at $RS (…:1:225439) · nổ 1 lần
//   · 05/08 · /inventory/items   · at $RS (…:1:343870) · nổ 1 lần  ← bản 2045016, mới hơn
// Hai route KHÁC HẲN nhau (lịch hẹn ↔ danh mục vật tư, không chung khối nội dung nào) mà cùng nổ,
// mỗi nơi đúng MỘT lần. Soi lại lần nữa phía mình: cả app KHÔNG chỗ nào gỡ/di chuyển node của
// người khác — chỉ có mẹo tạo thẻ <a> để tải file rồi tự xoá ĐÚNG THẺ CỦA NÓ (ExportCsv*, a.remove()),
// shell/layout không chèn <script> tay nào. Trước đó đã soi lồng-thẻ-không-hợp-lệ ở /appointments
// (Guide/InfoBox đặt <ol>/<b> trong <div> chứ không trong <p>; <tbody> chỉ chứa <tr>; <form> không
// lồng <form>) cũng sạch. ⇒ Không phải markup của một trang nào ⇒ đúng là nhiễu môi trường bên
// ngoài (tiện ích trình duyệt · trình duyệt in-app · proxy lọc HTML), CÙNG HỌ với #418 · zaloJSV2.
//
// Nhưng vé mẫu ấy lại đẻ một PHIẾU LỖI cho MỖI route mới gặp — người đi truy mất công mà không có
// gì để vá. ⇒ ĐỔI LUẬT: im với lỗi LẺ TẺ, chỉ kêu khi nó DỒN LẠI. Vì "dồn" mới là dấu của bug thật
// phía mình; còn nếu thật sự có trang gãy, $RS/$RC hụt thẻ tạm ở nhiều khối nội dung liên tiếp →
// vượt ngưỡng ngay. Ra ĐÚNG 1 vé ở lượt vượt ngưỡng, VÉ CÓ GHI SỐ LẦN, rồi im lại → vẫn có tín
// hiệu, không lụt hộp lỗi, và người mở vé biết ngay đây là ca DỒN chứ không phải ca lẻ tẻ.
// KHÔNG trông vào bộ gộp-trùng của bộ chung: fingerprint gồm khung stack đầu, mà khung này mang
// số cột đổi theo từng lượt render luồng (…:1:225439 / …:1:225512) ⇒ mỗi lượt là một vé MỚI.
//
// ── 05/08 (bản 2 — SỬA THƯỚC): đếm theo PHIÊN TAB là ĐO NHẦM, đã đẻ vé oan ở /inventory ──
// Bản 1 gom MỌI lượt nhiễu trong tab vào MỘT con số duy nhất: không tách route, không hạn thời
// gian. Nhưng chính ghi chú ngay trên đã chốt hình dạng của NHIỄU là "mỗi route nổ ĐÚNG 1 lượt" —
// mà nhân viên phòng khám mở MỘT tab chạy suốt buổi, đi qua vài chục route ⇒ 4 lượt LẺ TẺ ở 4
// route KHÁC NHAU vẫn cộng đủ 4 ⇒ chuông kêu "trang thật sự gãy" trong khi không trang nào gãy.
// Vé thật ở /inventory (bản 96fda62) đúng là ca đó: route THỨ BA, vẫn "nổ 1 lần", mà vé lại mang
// câu "đã LẶP tới lần thứ 4". Đã soi lại /inventory theo đúng lời vé dặn — markup trang sạch
// (Guide/InfoBox để <ol>/<b> trong <div>; Table đúng thead/tbody/tr/td; MList là <ul>/<li>), kit
// @suga-co/ui cũng không lồng thẻ sai ⇒ KHÔNG có gì để vá ở trang. Chuông báo NHẦM, lỗi ở thước.
//
// ⇒ ĐỔI THƯỚC cho đúng thứ mình định đo. "Trang thật sự gãy" thì $RS/$RC hụt thẻ tạm NGAY TRÊN
// TRANG ĐÓ, và hụt lại mỗi lần mở lại chính trang đó — tức DỒN TRÊN CÙNG MỘT ROUTE và GẦN NHAU VỀ
// THỜI GIAN. Còn nhiễu môi trường thì rải mỏng, mỗi nơi 1 lượt, cách nhau hàng giờ. Vậy:
//   ① đếm RIÊNG TỪNG ROUTE (route đang mở, không phải tổng cả tab);
//   ② chỉ tính các lượt nằm trong CÙNG một cửa sổ 30 phút — tab mở cả ngày không cộng dồn được nữa.
// Vẫn giữ nguyên 2 tính chất cũ: bộ đếm ở `sessionStorage` (sống qua các lượt TẢI TRANG trong cùng
// tab — để module scope là mỗi lượt tải về 0 ⇒ nuốt trắng vĩnh viễn, bản 8230f67 đã trả giá), và
// vé tự khai số lần + route để người mở vé không đóng nhầm.
//
// ── 05/08 (bản 3 — VÁ 3 CHỖ THƯỚC BẢN 2 CÒN LỆCH, mắt mới soi ra) ───────────
// Bản 2 đúng hướng (tách route + hạn giờ) nhưng cả 3 mắt xích đều rò, và rò về CÙNG MỘT PHÍA
// nguy hiểm nhất: NUỐT TRẮNG — thứ mà chính ghi chú trên thề không làm.
//   ① đọc sổ hụt → trả `{}` thay vì bản trong bộ nhớ. Trình duyệt kiểu Safari riêng tư đời cũ cho
//      `getItem` trả null nhưng `setItem` NÉM ⇒ mỗi lượt bắn đọc ra rỗng, đếm mãi = 1, ngưỡng 4
//      không bao giờ chạm. (Ca ⑥ cũ chỉ chặn kiểu chặn-cả-hai nên lọt.)
//   ② cửa sổ 30 phút là cửa sổ TRƯỢT: mốc bị đẩy theo mỗi lượt, nên 4 lượt cách nhau 29 phút trải
//      2 tiếng vẫn dồn đủ 4 và vé in "trong vòng 30 phút" — vé khai SAI, và báo động giả ở trang
//      nhân viên mở đi mở lại cả buổi (/inventory) chỉ hẹp lại chứ chưa chết. Nay đo từ LƯỢT ĐẦU.
//   ③ khoá sổ lấy `pathname` thô ⇒ route động bị chẻ nhỏ: một template gãy thật `/patients/[id]`
//      nổ ở /patients/<uuid A>, /patients/<uuid B>… mỗi ID một rổ 1 lượt ⇒ không rổ nào chạm
//      ngưỡng (lại còn đảo trần 20 route liên tục, hất luôn rổ của trang khác). Nay chuẩn hoá về
//      khuôn `/patients/:id` trước khi làm khoá — tiện thể chặn ID chảy vào câu vé + `source`.
const NUOT_TOI_LUOT = 3;

/**
 * Cửa sổ tính "DỒN": hai lượt cách nhau quá lâu thì không còn là dồn, coi như hai ca lẻ tẻ.
 * 30 phút — đủ rộng để bắt trọn một buổi làm việc trên cùng một trang, đủ hẹp để một tab mở
 * cả ngày không tự cộng dồn thành báo động giả.
 * ⚠️ Đo từ LƯỢT ĐẦU của cửa sổ (`mocDau`), KHÔNG phải từ lượt gần nhất — làm mới mốc theo mỗi lượt
 * là thành cửa sổ TRƯỢT, cứ nổ đều dưới 30 phút một lần thì dồn vô tận mà vé vẫn khai "trong vòng
 * 30 phút" (sai sự thật) ⇒ đúng cái báo động giả bản 2 định diệt.
 */
const CUA_SO_DON_MS = 30 * 60 * 1000;

/**
 * Mã của các HỌ NHIỄU CÓ SỔ ĐẾM (khoá rổ = `<mã họ>@<route>`, xem `khoaRo`). Khai TRƯỚC
 * `demThemMotLuot` vì hàm dọn sổ phải nhận ra mục LẠ ĐỊNH DẠNG mà bỏ đi.
 * (`NHIEU_ZALO_IN_APP` không có ở đây: nó nuốt thẳng, không đếm dồn.)
 */
const MA_HO_SCRIPT_LUONG_REACT = "script-luong-react";
const MA_HO_MAT_MANG_VET_TRUI = "mat-mang-vet-trui";
const MA_HO_CO_SO: ReadonlySet<string> = new Set([
  MA_HO_SCRIPT_LUONG_REACT,
  MA_HO_MAT_MANG_VET_TRUI,
]);

/**
 * Trần số RỔ nhớ cùng lúc, áp RIÊNG TỪNG HỌ — chặn sổ đếm phình ra khi người dùng đi qua nhiều trang.
 * Một rổ = MỘT HỌ NHIỄU trên MỘT route (xem `khoaRo`), không phải một route.
 *
 * ⚠️ TRẦN PHẢI THEO HỌ, không phải một trần chung cho cả sổ. 14/08 khoá sổ đổi từ route trần sang
 * họ×route mà trần vẫn giữ 20 cho TOÀN SỔ ⇒ hai chỗ hỏng cùng lúc: (a) sức nhớ mỗi họ tụt còn ~10
 * route, rổ bị hất sớm nên `NUOT_TOI_LUOT` khó chạm hơn; (b) tệ hơn — một họ nổ dày (rớt mạng cả
 * buổi) HẤT sạch rổ của họ kia, tức mất đúng tín hiệu "dồn trên cùng một trang" mà bộ đếm sinh ra
 * để bắt. Sổ vẫn có trần tổng: ≤ TRAN_RO_MOI_HO × số họ.
 */
const TRAN_RO_MOI_HO = 20;

/**
 * Khoá sổ đếm trong `sessionStorage` — sổ phải sống qua các lượt TẢI TRANG trong cùng tab
 * (chết khi đóng tab). Cùng họ (và cùng tiền tố `hd:`) với KHOA_SO_LUOT_TAI_LAI ở
 * lib/loi/lech-phien-ban.ts.
 */
const KHOA_DEM_NHIEU_LUONG = "hd:rs-noise-count";

/**
 * Sổ đếm: RỔ (họ nhiễu × route) → { lan: số lượt đã dồn · mocDau: lượt ĐẦU của cửa sổ (thước hết
 * hạn) · moc: lượt gần nhất (chỉ để xếp thứ tự khi phải hất bớt rổ theo trần) }.
 */
type MucDem = { lan: number; moc: number; mocDau: number };
type SoDem = Record<string, MucDem>;

/**
 * Dự phòng khi `sessionStorage` không dùng được (chế độ riêng tư / chặn cookie / hết quota):
 * còn đếm được trong lượt tải này. MỌI đường đọc-hụt đều phải lùi về đây, kể cả khi `getItem`
 * trả rỗng: có trình duyệt cho đọc (trả null) nhưng NÉM ở `setItem` ⇒ trả `{}` là bộ đếm reset
 * mỗi lượt bắn, ngưỡng không bao giờ chạm, nuốt trắng vĩnh viễn. Lượt tải đầu nó vốn là `{}`
 * nên lùi về đây vô hại.
 */
let soDemDuPhong: SoDem = {};

function docSoDem(): SoDem {
  try {
    const tho = sessionStorage.getItem(KHOA_DEM_NHIEU_LUONG);
    if (!tho) return soDemDuPhong; // chưa có sổ (hoặc ghi không xuống được) → bản trong bộ nhớ
    const o: unknown = JSON.parse(tho);
    return o && typeof o === "object" && !Array.isArray(o) ? (o as SoDem) : soDemDuPhong;
  } catch {
    return soDemDuPhong; // sessionStorage bị chặn / sổ hỏng → lùi về bản trong bộ nhớ
  }
}

function ghiSoDem(so: SoDem): void {
  soDemDuPhong = so; // luôn giữ bản trong bộ nhớ → sessionStorage chặn vẫn đếm được trong lượt tải
  try {
    sessionStorage.setItem(KHOA_DEM_NHIEU_LUONG, JSON.stringify(so));
  } catch {
    /* bị chặn → đã có bản trong bộ nhớ ở trên */
  }
}

/**
 * Đoạn đường dẫn này là ID chứ không phải tên trang? — số thuần (`/receipt/12`) hoặc chuỗi hex/dấu
 * gạch đủ dài (`/patients/3f2b1c4a-9d8e-…`; id nghiệp vụ trong DB là `uuid`). Đòi có ÍT NHẤT MỘT
 * CHỮ SỐ để khỏi nuốt nhầm tên trang viết toàn chữ a–f.
 */
const DOAN_LA_ID = /^(?:\d+|(?=[0-9a-f-]*\d)[0-9a-f-]{8,})$/i;

/**
 * Đổi đường dẫn THẬT thành KHUÔN route: `/patients/3f2b…/consent/91af…` → `/patients/:id/consent/:id`.
 * Bắt buộc, vì khoá sổ mà lấy đường dẫn nguyên văn thì:
 *   · một template gãy THẬT (`/patients/[id]`) bị chẻ thành mỗi ID một rổ, rổ nào cũng đúng 1 lượt
 *     ⇒ không bao giờ chạm ngưỡng (nuốt trắng), lại đảo trần TRAN_RO_MOI_HO liên tục nên hất sớm
 *     cả rổ của các route khác;
 *   · `duong` đi thẳng vào câu vé lẫn `source` ⇒ ID bệnh nhân chảy vào hộp lỗi.
 */
function chuanHoaDuong(duong: string): string {
  return duong
    .split("/")
    .map((doan) => (DOAN_LA_ID.test(doan) ? ":id" : doan))
    .join("/");
}

/** Route đang mở (ĐÃ chuẩn hoá về khuôn) — thước đo "dồn trên CÙNG một trang". */
function routeDangMo(): string {
  try {
    const duong = window.location?.pathname;
    return duong ? chuanHoaDuong(duong) : "(không rõ)";
  } catch {
    return "(không rõ)";
  }
}

/**
 * Khoá RỔ đếm = HỌ NHIỄU + ROUTE.
 *
 * ⚠️ SỔ PHẢI RIÊNG TỪNG HỌ. Gộp chung một sổ theo route thì 2 lượt nhiễu $RS + 2 lượt rớt mạng
 * trên cùng một trang cộng thành 4 ⇒ vượt ngưỡng ⇒ đẻ một vé đổ oan cho MỘT họ bằng chứng cứ của
 * họ KIA (vé khai "trang gãy, soi thẻ tạm" trong khi thủ phạm là wifi, hoặc ngược lại). Đúng họ
 * bệnh của vé oan /inventory, chỉ đổi trục: ở đó là gộp nhầm ROUTE, ở đây là gộp nhầm HỌ.
 */
function khoaRo(maHo: string, duong: string): string {
  return `${maHo}@${duong}`;
}

/** Lấy lại mã HỌ từ khoá rổ (`<mã họ>@<route>`); "" nếu khoá không đúng định dạng đó. */
function hoCuaKhoa(khoa: string): string {
  const cat = khoa.indexOf("@");
  return cat < 0 ? "" : khoa.slice(0, cat);
}

/**
 * Ghi nhận thêm 1 lượt nhiễu vào MỘT RỔ (họ nhiễu × route đang mở); trả về số lượt đã dồn của
 * CHÍNH rổ đó trong cửa sổ CUA_SO_DON_MS TÍNH TỪ LƯỢT ĐẦU của cửa sổ. Hết cửa sổ thì mục bị bỏ và
 * lượt này mở cửa sổ mới (đếm lại từ 1). Mục quá hạn của rổ khác cũng bị dọn ⇒ sổ tự gọn theo
 * thời gian.
 */
function demThemMotLuot(ro: string): number {
  const bayGio = Date.now();
  const so = docSoDem();
  for (const k of Object.keys(so)) {
    const m = so[k] as Partial<MucDem> | undefined;
    if (!m || typeof m.lan !== "number" || typeof m.moc !== "number") {
      delete so[k];
      continue;
    }
    // Mục LẠ ĐỊNH DẠNG → bỏ luôn. Sổ bản trước 14/08 khoá theo ROUTE TRẦN ("/inventory/items") mà
    // TÊN KHO giữ nguyên (`hd:rs-noise-count`), nên tab mở xuyên bản mới còn những mục đó: khoá mới
    // là `<họ>@<route>` ⇒ chúng không bao giờ được cộng thêm nữa, giữ lại chỉ tổ chiếm trần và hất
    // oan rổ đang sống. (Dọn theo hạn cũng xoá được, nhưng phải chờ hết cửa sổ 30 phút.)
    if (!MA_HO_CO_SO.has(hoCuaKhoa(k))) {
      delete so[k];
      continue;
    }
    // Sổ bản CŨ (chưa có `mocDau`) còn sót trong tab đang mở lúc lên bản mới → tạm lấy mốc gần nhất.
    if (typeof m.mocDau !== "number") m.mocDau = m.moc;
    if (bayGio - m.mocDau > CUA_SO_DON_MS) delete so[k];
  }
  const cu = so[ro];
  const lan = cu ? cu.lan + 1 : 1;
  // `mocDau` GIỮ NGUYÊN suốt cửa sổ (mục quá hạn đã bị dọn ở trên nên `cu` chắc chắn còn trong cửa
  // sổ) — làm mới nó theo từng lượt là quay lại cửa sổ trượt, dồn vô tận.
  so[ro] = { lan, moc: bayGio, mocDau: cu ? cu.mocDau : bayGio };
  // Trần áp RIÊNG TỪNG HỌ (xem `TRAN_RO_MOI_HO`): chỉ họ vừa thêm rổ mới có thể vượt trần, và chỉ
  // rổ của CHÍNH họ đó bị hất — họ kia không mất trí nhớ vì họ này nổ dày. Bỏ mục CŨ NHẤT (rổ đang
  // xét luôn mới nhất nên an toàn).
  const maHo = hoCuaKhoa(ro);
  const cungHo = Object.keys(so).filter((k) => hoCuaKhoa(k) === maHo);
  if (cungHo.length > TRAN_RO_MOI_HO) {
    cungHo.sort((a, b) => (so[a]?.moc ?? 0) - (so[b]?.moc ?? 0));
    for (const k of cungHo.slice(0, cungHo.length - TRAN_RO_MOI_HO)) delete so[k];
  }
  ghiSoDem(so);
  return lan;
}

/** Đang TỰ dựng vé báo-lặp? → bộ lọc phải CHO QUA, kẻo vé của chính ta bị chính ta nuốt (đệ quy). */
let dangKeuVeLap = false;

/**
 * Một HỌ NHIỄU chịu luật "im khi lẻ tẻ, kêu khi dồn": mẫu khớp VẾT LỖI, mã rổ đếm (sổ RIÊNG từng
 * họ — xem `khoaRo`) và câu vé lúc vượt ngưỡng.
 * `cauVe` phải nói ĐÚNG thủ phạm của CHÍNH họ đó: vé "$RS" bảo người ta soi thẻ tạm của trang, vé
 * "mất mạng" bảo soi đường truyền/máy chủ. Dùng chung một câu là đẩy người truy đi sai cửa.
 * ⚠️ `khop` là HÀM chứ không phải RegExp: họ "mất mạng" phải soi vết bằng CHÍNH danh sách mẫu ở
 * lib/loi/khong-toi-may-chu.ts, mà ghép chúng lại thành một RegExp là tự chuốc bộ lọc chết câm
 * (xem `laVetTruiMatMang`). `khop` KHÔNG được nhớ trạng thái giữa 2 lượt gọi — mẫu mang cờ `g`/`y`
 * nhớ `lastIndex` ⇒ khớp lúc có lúc không.
 */
type HoNhieu = {
  ma: string;
  khop: (vet: string) => boolean;
  cauVe: (lan: number, duong: string, cauGoc: string, phut: number) => string;
};

const HO_SCRIPT_LUONG_REACT: HoNhieu = {
  ma: MA_HO_SCRIPT_LUONG_REACT,
  khop: (vet) => NHIEU_SCRIPT_LUONG_REACT.test(vet),
  cauVe: (lan, duong, cauGoc, phut) =>
    `Nhiễu script luồng react-dom ($RS/$RC/$RR) đã DỒN ${lan} lượt trên CÙNG route ${duong} trong ` +
    `vòng ${phut} phút (${NUOT_TOI_LUOT} lượt đầu đã bị nuốt; đếm dồn qua các lần tải CHÍNH route ` +
    `đó trong cùng một tab). ⚠️ ĐỪNG đóng vé này bằng lý do "nhiễu môi trường": nhiễu là mỗi route ` +
    `nổ LẺ TẺ 1 lượt rồi thôi, còn DỒN nhiều lượt trên CÙNG một trang trong thời gian ngắn mới là ` +
    `dấu trang thật sự gãy — soi khối nội dung/thẻ tạm của ${duong}. Lỗi gốc: ${cauGoc}`,
};

const HO_MAT_MANG_VET_TRUI: HoNhieu = {
  ma: MA_HO_MAT_MANG_VET_TRUI,
  khop: laVetTruiMatMang,
  cauVe: (lan, duong, cauGoc, phut) =>
    `Lời gọi máy chủ KHÔNG ĐI ĐƯỢC (họ "mất mạng", vết lỗi TRỤI — không một khung nào để lần ra nơi ` +
    `gọi) đã DỒN ${lan} lượt trên CÙNG route ${duong} trong vòng ${phut} phút (${NUOT_TOI_LUOT} lượt ` +
    `đầu đã bị nuốt; đếm dồn qua các lần tải CHÍNH route đó trong cùng một tab). ⚠️ ĐỪNG đóng vé này ` +
    `bằng lý do "người dùng rớt wifi": rớt mạng lẻ tẻ thì mỗi trang 1 lượt rồi thôi, còn DỒN nhiều ` +
    `lượt trên CÙNG một trang trong thời gian ngắn mới là dấu ĐƯỜNG TRUYỀN CỦA MÌNH đang hỏng — soi ` +
    `log máy chủ quanh giờ này (Railway khởi động lại, proxy cắt giữa chừng, endpoint sai). Lỗi gốc: ` +
    `${cauGoc}`,
};

/**
 * Dựng vé "đã DỒN N lượt trên CÙNG route X trong 30 phút", mang theo vết lỗi gốc để còn truy.
 * Vì sao phải TỰ dựng thay vì trả `false` cho bộ chung dựng hộ: `test()` chỉ trả boolean nên
 * KHÔNG gắn được ngữ cảnh vào vé. Vé do bộ chung dựng trông y hệt một vết `$RS` (hay một câu
 * "network error") nổ 1 lần — đúng cái mẫu mà chính ghi chú trên dạy là "nhiễu môi trường" ⇒ người
 * truy (hoặc phiên sau) đóng nhầm rồi quay lại vòng lặp cũ. Vé phải TỰ KHAI nó đã vượt ngưỡng, và
 * khai RÕ TRÊN ROUTE NÀO — thiếu chỗ đó là người truy đi soi nhầm trang (bệnh của vé oan /inventory).
 * `duong` là KHUÔN route đã chuẩn hoá (`/patients/:id`), không phải đường dẫn thật — vừa đúng thứ
 * cần soi (template gãy chứ không phải một hồ sơ cụ thể), vừa khỏi rò ID vào hộp lỗi.
 */
function keuVeLap(ho: HoNhieu, vet: string, lan: number, duong: string): void {
  const cauGoc = vet.split("\n", 1)[0]?.trim() || "(không có câu lỗi)";
  const phut = Math.round(CUA_SO_DON_MS / 60000);
  const e = new TypeError(ho.cauVe(lan, duong, cauGoc, phut));
  e.stack = vet;
  dangKeuVeLap = true;
  try {
    clientErrorReporter.report(e, { source: `loc-nhieu-${ho.ma}:${duong}:lan-${lan}` });
  } finally {
    dangKeuVeLap = false;
  }
}

/**
 * Bọc 1 HỌ NHIỄU thành bộ lọc CHỈ KÊU KHI DỒN TRÊN CÙNG MỘT ROUTE, vẫn là RegExp để truyền vào
 * `ignore` của bộ chung (bộ chung chỉ gọi `.test()`). Nuốt mọi lượt khớp; đúng ở lượt vượt ngưỡng
 * (`nuotToi + 1`) của rổ đó thì tự dựng MỘT vé có ghi số lần + route rồi thôi ⇒ 1 vé duy nhất
 * mỗi rổ mỗi cửa sổ, và vé ấy tự khai vì sao nó ra.
 */
function locKhiDonMotTrang(ho: HoNhieu, nuotToi: number): RegExp {
  // Vỏ RegExp chỉ để lọt kiểu `ignore?: RegExp[]` của bộ chung — bộ chung CHỈ gọi `.test()`
  // (`ignore.some((re) => re.test(s))`), phần MẪU của vỏ không ai đọc. Để `(?!)` (không khớp gì)
  // cho chắc: ai lỡ dùng mẫu vỏ thay vì `.test` thì bỏ sót vài lượt, chứ không nuốt oan vé thật.
  const loc = /(?!)/;
  loc.test = (chuoi: string): boolean => {
    if (dangKeuVeLap) return false; // vé báo-dồn của chính ta → cho qua, đừng đếm lại
    if (!ho.khop(chuoi)) return false; // không phải họ nhiễu này → để bộ chung xử bình thường
    const duong = routeDangMo();
    const lan = demThemMotLuot(khoaRo(ho.ma, duong));
    if (lan === nuotToi + 1) keuVeLap(ho, chuoi, lan, duong);
    return true; // bản gốc LUÔN bị nuốt — vé (nếu tới ngưỡng) đã tự dựng ở trên, kèm số lần + route
  };
  return loc;
}

// 1 instance / tab (module scope) → GIỮ trạng thái gộp-trùng + trần-mỗi-phiên qua các lần re-render.
// Export để error.tsx (error boundary) BÁO TAY lỗi render dùng CHUNG bộ gộp-trùng này.
export const clientErrorReporter = createClientErrorReporter({
  ignore: [
    NHIEU_ZALO_IN_APP,
    locKhiDonMotTrang(HO_SCRIPT_LUONG_REACT, NUOT_TOI_LUOT),
    locKhiDonMotTrang(HO_MAT_MANG_VET_TRUI, NUOT_TOI_LUOT),
  ],
  // Dấu bản build → hiện thành dòng "• Phiên bản: …" trong mô tả vé Hộp Lỗi.
  // PHẢI viết NGUYÊN VĂN `process.env.NEXT_PUBLIC_APP_VERSION` ở đây thì trình đóng gói mới thay
  // được bằng chuỗi thật. KHÔNG trông vào việc bộ chung tự đọc env: nó đọc qua biến trung gian
  // (`penv.NEXT_PUBLIC_APP_VERSION` trong observe-web/client) — dạng đó trình đóng gói KHÔNG
  // nhận ra để thay ⇒ ở trình duyệt sẽ rỗng, và ta lại tưởng "đã có nhãn" trong khi không có.
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
});

export function ClientErrorReporter() {
  useEffect(() => {
    // install() gắn cả 2 đường (window 'error' + vá console.error) VÀ tự gắn dấu vân tay môi
    // trường cho họ lỗi hydrate — trả hàm GỠ → React tự dọn khi unmount.
    return clientErrorReporter.install();
  }, []);
  return null;
}
